import InputSlider from '@/components/sections/builder/settings/input-slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getFonts } from '@/services/site-api';
import { ThemeTypographyType, useBuilderStore } from '@/stores/builder';
import { useQuery } from '@tanstack/react-query';
import { CheckCircleIcon, SearchIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { shallow } from 'zustand/shallow';
import { ViewportList } from 'react-viewport-list';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import ModalResponsive, { ModalResponsiveFooter } from '@/components/commons/modal-responsive';
import { ReloadIcon } from '@radix-ui/react-icons';
import GoogleFontLoader from 'react-google-font-loader';

type VariantType = {
  name: string;
  weight: number;
  style: string;
};

export const variants = {
  '300': { name: 'Light', weight: 300, style: 'normal' },
  '300Italic': { name: 'Light Italic', weight: 300, style: 'italic' },
  regular: { name: 'Regular', weight: 400, style: 'normal' },
  italic: { name: 'Italic', weight: 400, style: 'italic' },
  '500': { name: 'Medium', weight: 500, style: 'normal' },
  '500Italic': { name: 'Medium Italic', weight: 500, style: 'italic' },
  '600': { name: 'Semibold', weight: 600, style: 'normal' },
  '600Italic': { name: 'Semibold Italic', weight: 600, style: 'italic' },
  '700': { name: 'Bold', weight: 700, style: 'normal' },
  '700Italic': { name: 'Bold Italic', weight: 700, style: 'italic' },
  '800': { name: 'Extra Bold', weight: 800, style: 'normal' },
  '800Italic': { name: 'Extra Bold Italic', weight: 800, style: 'italic' },
} as {
  [x: string]: VariantType;
};

type FontItemType = {
  family: string;
  variants: string[];
  files: string[];
  category: string;
};

type SelectFontsType = {
  defaultValue: { fontFamily: string | undefined; fontVariant: string | undefined };
  items: FontItemType[];
  openModal?: boolean;
  closeModal?: () => void;
  onSelect: (font: FontItemType, variant: string) => void;
};

const PreviewFont = ({ item, isSelected }: { item: FontItemType; isSelected: boolean }) => (
  <>
    <GoogleFontLoader fonts={[{ font: item.family }]} />
    <div
      className='col-span-1 flex cursor-pointer justify-between border-b px-6 py-4 hover:bg-slate-100'
      style={{ fontFamily: item.family }}
    >
      <span className='text-base'>{item.family}</span>
      {isSelected && <CheckCircleIcon className='h-4 w-4 text-green-600' />}
    </div>
  </>
);

const ModalSelectFonts = ({
  defaultValue,
  items,
  openModal,
  closeModal,
  onSelect,
}: SelectFontsType) => {
  const [searchValue, setSearchValue] = useState('');
  const [page, setPage] = useState(1);
  const [fonts, setFonts] = useState<FontItemType[]>([]);
  const [selected, setSelected] = useState<FontItemType>({} as FontItemType);
  const [selectedVariant, setSelectedVariant] = useState('');

  const ref = useRef(null);

  useEffect(() => {
    setFonts(items);
  }, [items]);

  useEffect(() => {
    if (!openModal) setPage(1);
  }, [openModal]);

  useEffect(() => {
    if (searchValue) {
      const filtered = items.filter((i) =>
        i.family?.toLowerCase().includes(searchValue.toLowerCase())
      );
      setFonts(filtered);
    } else {
      setFonts(items);
    }
  }, [searchValue]);

  useEffect(() => {
    const findSelected =
      items.find((obj) => obj.family === defaultValue.fontFamily) || ({} as FontItemType);
    setSelected(findSelected);
    setSelectedVariant(defaultValue.fontVariant as string);
  }, [defaultValue]);

  const optVariants = useMemo(() => {
    const opt = [] as { value: string; name: string }[];
    selected.variants?.forEach((v: string) => {
      const tempVariant = variants[v] as VariantType;
      if (tempVariant) {
        opt.push({
          value: v,
          ...tempVariant,
        });
      }
    });
    return opt;
  }, [selected.variants]);

  const variantValue = useMemo(() => {
    return variants[selectedVariant] || variants.regular;
  }, [selectedVariant]);

  const fontsPaginated = fonts.slice(0, page * 10);

  return (
    <ModalResponsive
      openModal={openModal}
      title='Select Fonts'
      setOpenModal={(open) => (!open ? closeModal?.() : undefined)}
      className='max-w-md'
    >
      <div className='w-full px-6'>
        <Input
          name='search'
          type='text'
          placeholder='Search'
          className='w-full'
          leftContent={<SearchIcon className='mx-auto w-4' />}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
        />
      </div>
      <div className='h-96'>
        <div className='grid max-h-96 grid-cols-1 overflow-y-auto border-t' ref={ref}>
          {fontsPaginated.length > 0 ? (
            <>
              <ViewportList viewportRef={ref} items={fontsPaginated}>
                {(item, index) => (
                  <button
                    key={index}
                    type='button'
                    onClick={() => setSelected(item)}
                    className='w-full'
                  >
                    <PreviewFont item={item} isSelected={selected.family === item.family} />
                  </button>
                )}
              </ViewportList>
              {fontsPaginated.length < fonts.length && (
                <button
                  onClick={() => setPage(page + 1)}
                  className='flex w-full items-center justify-center gap-x-2 py-4 text-primary hover:bg-slate-100'
                >
                  <ReloadIcon className='h-4 w-4' />
                  Load more
                </button>
              )}
            </>
          ) : (
            <div className='flex justify-center py-4 font-bold'>No results found</div>
          )}
        </div>
      </div>
      <ModalResponsiveFooter className='pt-4'>
        <div className='w-full'>
          <div
            className='text-lg font-bold'
            style={{
              fontFamily: selected.family,
              fontStyle: variantValue.style,
              fontWeight: variantValue.weight,
            }}
          >
            {selected.family}
          </div>
          {selected.variants?.length > 1 ? (
            <div className='mt-1 pr-2'>
              <Select
                name='variant'
                value={selectedVariant || 'regular'}
                onValueChange={setSelectedVariant}
              >
                <SelectTrigger className='py-5'>
                  <SelectValue placeholder='Regular' />
                </SelectTrigger>
                <SelectContent>
                  {optVariants.map((v) => (
                    <SelectItem key={v.value} value={v.value}>
                      {v.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          ) : (
            <div className='mt-1'>Regular</div>
          )}
          <div className='mt-4 flex items-center justify-end space-x-3 pr-2'>
            <Button variant='default' size='sm' onClick={() => onSelect(selected, selectedVariant)}>
              Select
            </Button>
            <Button variant='outline' size='sm' onClick={close}>
              Cancel
            </Button>
          </div>
        </div>
      </ModalResponsiveFooter>
    </ModalResponsive>
  );
};

export default function Typography() {
  const [fontDisplay, setFontDisplay] = useState<null | 'heading' | 'body'>('heading');

  const builder = useBuilderStore(
    (state) => ({
      theme: state.theme,
      setTheme: state.setTheme,
    }),
    shallow
  );

  const typography: ThemeTypographyType = builder.theme?.typography || {};

  const setLayout = (newTypography: Partial<ThemeTypographyType>) => {
    builder.setTheme({
      typography: { ...typography, ...newTypography },
    });
  };

  const { data: dataFonts } = useQuery({
    queryKey: ['getFonts'],
    queryFn: getFonts,
  });

  const onSelectFont = (font: FontItemType, variant: string) => {
    const key = fontDisplay === 'heading' ? 'headingFont' : 'font';
    setLayout({
      [`${key}Family`]: font.family,
      [`${key}Variant`]: variant,
    });
    setFontDisplay(null);
  };

  useEffect(() => {
    setFontDisplay(null);
  }, []);

  const items = (dataFonts?.items as FontItemType[]) || [];

  return (
    <div>
      <GoogleFontLoader
        fonts={[
          { font: (typography?.headingFontFamily as string) || '' },
          { font: (typography?.fontFamily as string) || '' },
        ]}
      />
      <div className='mb-4 mt-4 font-bold'>Headings</div>
      <Select name='headingFonts'>
        <SelectTrigger className='py-5' onClick={() => setFontDisplay('heading')}>
          <SelectValue placeholder={typography?.headingFontFamily || 'Select'} />
        </SelectTrigger>
      </Select>
      <InputSlider
        label='Font size scale'
        value={typography?.headingFontScaleSize || 100}
        max={200}
        min={100}
        step={10}
        onChange={(val) =>
          setLayout({
            headingFontScaleSize: val,
          })
        }
      />
      <div className='mb-4 mt-4 font-bold'>Body</div>
      <Select name='bodyFonts'>
        <SelectTrigger className='py-5' onClick={() => setFontDisplay('body')}>
          <SelectValue placeholder={typography?.fontFamily || 'Select'} />
        </SelectTrigger>
      </Select>
      <InputSlider
        label='Font size scale'
        value={typography?.fontScaleSize || 100}
        max={200}
        min={100}
        step={10}
        onChange={(val) =>
          setLayout({
            fontScaleSize: val,
          })
        }
      />
      <ModalSelectFonts
        defaultValue={
          fontDisplay === 'heading'
            ? {
                fontFamily: typography?.headingFontFamily,
                fontVariant: typography?.headingFontFamily,
              }
            : { fontFamily: typography?.fontFamily, fontVariant: typography?.fontVariant }
        }
        items={items}
        onSelect={onSelectFont}
        openModal={!!fontDisplay}
        closeModal={() => setFontDisplay(null)}
      />
    </div>
  );
}
